import appConfig from '../../config/appConfig';
import * as cmAemFlexBatteryAdaptiveExpTypes from '../../consts/communicationLayer/cmAemFlexBatteryAdaptiveExpTypes';
import * as cmHapticProfileTypes from '../../consts/communicationLayer/cmHapticProfileTypes';
import * as cmOnOffTypes from '../../consts/communicationLayer/cmOnOffTypes';
import * as gamActivationStatusTypes from '../../consts/gam/activationStatusTypes';
import * as aemFlexBatteryModeTypes from '../../consts/iot/aemFlexBatteryModeTypes';
import * as autoStartModeTypes from '../../consts/iot/autoStartModeTypes';
import {
    FLEX_PUFF_ENGINE_JOULE_DISABLE,
    FLEX_PUFF_ENGINE_JOULE_ENABLED,
} from '../../consts/iot/flexPuffEngineJouleTypes';
import * as iotParentProtectionStatus from '../../consts/iot/iotParentProtectionStatus';
import * as responsiveDrawModeTypes from '../../consts/iot/responsiveDrawModeTypes';
import * as uamActivationStatusTypes from '../../consts/uam/activationStatusTypes';
import * as yapResponsiveDrawModeTypes from '../../consts/yap/responsiveDrawModeTypes';
import * as yapDeviceEventChangeTypes from '../../consts/yap/yapDeviceEventChangeTypes';
import * as yapHolderStatusTypes from '../../consts/yap/yapHolderStatusTypes';
import * as yapUpdateSettingsTypes from '../../consts/yap/yapUpdateSettingsTypes';
import DEVICE_TYPES from '../../enums/device/deviceTypes';
import {setIsAemFlexBatteryWarningVisible} from '../../state/ducks/global';
import {
    clearIotDeviceHolderData,
    setDeviceActivationStatus,
    setIsDeviceNotSupported,
    updateIotDeviceData,
} from '../../state/ducks/iotDevice/actions';
import {setExperienceDataRanges} from '../../state/ducks/usageStatistic';
import {setHolderInfoReceived, setYapAssets, setYapHolderConnected} from '../../state/ducks/yapEncrypted';
import {selectIotDeviceData, selectIotDeviceType, selectIotHolderType} from '../../state/selectors/iotDevice';
import {makeSelectConsumerProductMaterialByType} from '../../state/selectors/products';
import {
    makeSelectAssetByAssetId,
    makeSelectAssetByType,
    makeSelectIsHolderByAssetId,
    makeSelectIsYapUpdateSettingsInProgress,
    makeSelectRequestActionTypeData,
} from '../../state/selectors/yapEncrypted';
import {dispatch, getState} from '../../state/store';
import appNotificationsService from '../app/appNotificationsService';
import backendService from '../app/backendService';
import connectivityService from '../device/connectivityService';
import deviceAssetService from '../device/deviceAssetService';
import deviceTypesService from '../device/deviceTypesService';
import iccConsumerProducts from '../icc/iccConsumerProducts';
import iotDeviceService from '../iotDevice/iotDeviceService';
import log from '../logger/log';
import {getMediaId} from '../mapping/yapMappings';
import marketConfigService from '../marketConfigService';
import marketTypeService from '../marketTypeService';
import productHelpers from '../product/productHelpers';
import {READ_HOLDER_SERIAL_NUMBER_TIMEOUT_MS_BLE_ON_SYSTEM_STATUS_CHANGED} from '../scp/scpConsts';
import * as dataCollectionService from '../uam/dataCollectionService';
import * as connectCareService from './connectCareService';
import yapConnectionService from './yapConnectionService';
import yapService from './yapService';

const updateDeviceCharacteristics = async (event) => {
    const details = event?.details;

    if (!details) return;

    const isBle = connectivityService.isBle();

    switch (event.eventType) {
        case yapDeviceEventChangeTypes.SYSTEM_STATUS:
            //for USB inside details itself
            let {holderState, holderDefect} = details;
            const {holderPlugged, holderConnection, characteristics} = details;
            const holderPluggedIn = holderPlugged || holderConnection === 'CONNECTED';

            if (holderState === undefined) {
                const {chargerSystemStatus} = characteristics || {};
                const {holderState: systemHolderState, holderDefect: systemHolderDefect} = chargerSystemStatus || {};

                //for BLE inside characteristics.chargerSystemStatus node
                holderState = systemHolderState;
                holderDefect = systemHolderDefect;
            }

            const isHolderConnected = holderPluggedIn && holderState !== yapHolderStatusTypes.UNPLUGGED;
            const {device} = selectIotDeviceData(getState()) || {};
            const {isHolderConnected: isHolderConnectedPrevState} = device || {};

            const onHolderDisconnected = () => {
                dispatch(clearIotDeviceHolderData());
                dispatch(setYapHolderConnected(false));
                dispatch(setHolderInfoReceived(false));
                dataCollectionService.setDataCollectionFlowReceived({isHolder: true});
            };

            const updateHolderData = () => {
                const device = {isHolderConnected, holder_connected: holderPluggedIn, holder_defect: holderDefect};
                const holderData = {
                    holder_state: yapHolderStatusTypes.getIotHolderStatus(holderState),
                    holder_connected: holderPluggedIn,
                };

                dispatch(updateIotDeviceData({device, holder: holderData}));
            };

            if (!isHolderConnected) {
                onHolderDisconnected();
            } else if (!isHolderConnectedPrevState) {
                const {holderSerial, holderCodentify} = await yapConnectionService.initScpData({
                    isHolderOnly: true,
                    isHolderDetect: true,
                    readHolderSerialNumberTimeoutMs: isBle
                        ? READ_HOLDER_SERIAL_NUMBER_TIMEOUT_MS_BLE_ON_SYSTEM_STATUS_CHANGED
                        : undefined,
                });

                if (!holderSerial && !holderCodentify) {
                    onHolderDisconnected();
                    return;
                }

                updateHolderData();
            } else {
                updateHolderData();
            }
            break;
        case yapDeviceEventChangeTypes.BATTERY_STATUS:
            const {batteryChargeUnified, chargerBatteryVoltage} = details;

            if (!batteryChargeUnified) return;

            const deviceData = isBle
                ? {battery_charge: batteryChargeUnified}
                : {battery_charge: batteryChargeUnified, batteryVoltage: chargerBatteryVoltage};

            dispatch(updateIotDeviceData({device: deviceData}));
            break;
        default:
            break;
    }
};

const updateDeviceSettings = ({type, newValue, isHolder = false, isNeedInformUser = false}) => {
    const state = getState();
    const {newValue: actionData} = makeSelectRequestActionTypeData(isHolder)(state);
    const {newValue: actionValue, settingName} = actionData || {};
    const settingType = type ? type : settingName;
    const settingValue = type ? newValue : actionValue;

    if (settingType === undefined || settingValue === undefined) return;

    let data;

    switch (settingType) {
        case yapUpdateSettingsTypes.LED_INTENSITY:
            data = {ledData: {[isHolder ? 'holder' : 'device']: settingValue}};
            break;
        case yapUpdateSettingsTypes.PARENT_PROTECTION:
            const {protectionType} = settingValue;
            const isLocked = protectionType === iotParentProtectionStatus.PARENT_PROTECTION_ENABLED;

            data = {[isHolder ? 'holder' : 'device']: {parent_protection_mode: isLocked}};

            if (!isNeedInformUser) break;

            const isUpdateSettingsInProgress = makeSelectIsYapUpdateSettingsInProgress(isHolder)(state);

            if (!isUpdateSettingsInProgress) break;

            appNotificationsService.showLockNotification(isLocked);
            break;
        case yapUpdateSettingsTypes.VAPE_CLOUD_SIZE:
            const {heatingProfile} = settingValue;

            data = {vapeCloudSizeData: {vape_cloud_size: heatingProfile}};
            break;
        case yapUpdateSettingsTypes.SESSION_PROFILING:
            const {puffNumber, puffingTime} = settingValue;

            data = {sessionProfiling: {puff_number: puffNumber, puff_time: puffingTime}};
            break;
        case yapUpdateSettingsTypes.RESPONSIVE_DRAW_PROFILE:
            const {hapticPattern} = settingValue;
            let value = hapticPattern;

            if (hapticPattern === yapResponsiveDrawModeTypes.ALLEGRO) {
                value = responsiveDrawModeTypes.VIVACE;
            } else if (hapticPattern === yapResponsiveDrawModeTypes.VIVALDI) {
                value = responsiveDrawModeTypes.STACCATO;
            }

            data = {responsiveDrawData: {responsive_draw_profile: value}};
            break;
        case yapUpdateSettingsTypes.EXT_HAPTIC_PROFILE:
            const {durations} = settingValue;
            const extHapticProfileIsOn = durations ? durations[0]?.durationIsOn : undefined;

            data = {vibrationData: {extended_haptic_profile: extHapticProfileIsOn}};
            break;
        case yapUpdateSettingsTypes.HEATING_VIBRATION:
            const {endOfHeating, fullyHeated, nearEnd, startHeating, hapticProfile} = settingValue;

            data = {
                vibrationData: {
                    fully_heated: fullyHeated,
                    start_heating: startHeating,
                    almost_finished: nearEnd,
                    finished: endOfHeating,
                },
            };

            if (hapticProfile || hapticProfile === 0) {
                data.vibrationData.all_holder_vibrations = hapticProfile === cmHapticProfileTypes.ENABLED;
            }
            break;
        case yapUpdateSettingsTypes.UI_HEATING_VIBRATION:
            const {preHeatVibration, heatingVibration, nearEndHeatVibration, heatingEndVibration} = settingValue;

            data = {
                vibrationData: {
                    fully_heated: convertIntToBoolSetting(heatingVibration),
                    start_heating: convertIntToBoolSetting(preHeatVibration),
                    almost_finished: convertIntToBoolSetting(nearEndHeatVibration),
                    finished: convertIntToBoolSetting(heatingEndVibration),
                },
            };
            break;
        case yapUpdateSettingsTypes.BATTERY_CHARGED_VIBRATION:
            const {batteryCharged} = settingValue;

            data = {vibrationData: {battery_charged: batteryCharged}};
            break;
        case yapUpdateSettingsTypes.UI_BATTERY_CHARGED_VIBRATION:
            const {batteryChargedOneExp, batteryChargedTwoExp} = settingValue;
            const isOn = batteryChargedOneExp === cmOnOffTypes.ON && batteryChargedTwoExp === cmOnOffTypes.ON;

            data = {vibrationData: {start_charging: isOn}};
            break;
        case yapUpdateSettingsTypes.LIFT_UP_GESTURE:
            data = {gestureModeData: {holder: {status_gesture_pickup_enabled: getGestureState(settingValue)}}};
            break;
        case yapUpdateSettingsTypes.DOUBLE_TAP_GESTURE:
            data = {gestureModeData: {holder: {status_gesture_double_tap_enabled: getGestureState(settingValue)}}};
            break;
        case yapUpdateSettingsTypes.AUTO_START_HEATING:
            const {featureState} = settingValue;
            const autoStartValue =
                featureState === cmOnOffTypes.ON
                    ? autoStartModeTypes.AUTO_START_STATE_ENABLED
                    : autoStartModeTypes.AUTO_START_STATE_DISABLE;

            data = {autoStartData: {holder: {autostart_feature_state: autoStartValue}}};
            break;
        case yapUpdateSettingsTypes.FLEX_PUFF_ENGINE_JOULE:
            const {featureState: engineJouleValue} = settingValue;
            const flexPuffValue =
                engineJouleValue === cmOnOffTypes.ON ? FLEX_PUFF_ENGINE_JOULE_ENABLED : FLEX_PUFF_ENGINE_JOULE_DISABLE;

            data = {flexPuff: flexPuffValue};
            break;
        case yapUpdateSettingsTypes.AEM_FLEX_BATTERY_ADAPTIVE_EXP:
            const {mode: adaptiveExpManagement} = settingValue;
            const aemDataValue =
                adaptiveExpManagement === cmAemFlexBatteryAdaptiveExpTypes.ECO_MODE
                    ? aemFlexBatteryModeTypes.ECO_MODE
                    : aemFlexBatteryModeTypes.PERFORMANCE_MODE;
            const iotDevice = selectIotDeviceData(state);
            const {aemData} = iotDevice || {};
            const {flexBatteryMode} = aemData || {};

            //show warning after mode change
            if (!!flexBatteryMode && flexBatteryMode !== aemDataValue) {
                dispatch(setIsAemFlexBatteryWarningVisible(true));
            }

            data = {aemData: {flexBatteryMode: aemDataValue}};
            break;
        case yapUpdateSettingsTypes.AEM_FLEX_BATTERY_PAUSE_HEATING:
            const {featureState: pauseHeating} = settingValue;
            const pauseHeatingValue = pauseHeating === cmOnOffTypes.ON;

            data = {aemData: {isPause: pauseHeatingValue}};
            break;
        default:
            break;
    }

    if (data) {
        dispatch(updateIotDeviceData(data));
    }
};

const getGestureState = (settingValue) => {
    const {gestureState} = settingValue;

    return gestureState === cmOnOffTypes.ON;
};

const convertIntToBoolSetting = (value) => {
    if (!value) return false;

    return value > 0;
};

const updateExperienceDataRanges = (isHolder, experienceDataRanges, usageLatestIndex) => {
    const deviceType = selectIotDeviceType(getState());
    const isHolderExperiences = deviceTypesService.isP1V4(deviceType) && !deviceTypesService.isMono(deviceType);

    if ((isHolderExperiences && isHolder) || (!isHolder && !isHolderExperiences)) {
        dispatch(setExperienceDataRanges(usageLatestIndex || experienceDataRanges));
    }
};

const checkIsDeviceActivated = async () => {
    const isYAPFlowEnabled = yapService.isYapMode();

    if (!isYAPFlowEnabled) {
        dispatch(setDeviceActivationStatus(true));
        return;
    }

    const state = getState();
    const {device} = selectIotDeviceData(state);
    const {isP4} = device || {};

    if (!isP4) {
        dispatch(setDeviceActivationStatus(true));
        return;
    }

    let isDeviceActivated = false;
    const asset = makeSelectAssetByType()(state);

    if (asset?.assetId) {
        isDeviceActivated = asset.yapActivated;
    }

    log.info(`checkIsDeviceActivated: ${isDeviceActivated}`);
    dispatch(setDeviceActivationStatus(isDeviceActivated));
};

const updateActivationStatus = async (yapActivationStatus) => {
    const state = getState();
    const asset = makeSelectAssetByType()(state);

    if (yapActivationStatus && asset?.assetId) {
        asset.yapActivated = backendService.isGamBackend()
            ? yapActivationStatus.toLowerCase() === gamActivationStatusTypes.GAM_ACTIVATED ||
              yapActivationStatus.toLowerCase() === gamActivationStatusTypes.GAM_ACTIVE
            : yapActivationStatus.toLowerCase() === uamActivationStatusTypes.UAM_ACTIVATED;
        dispatch(setYapAssets(asset));
        await checkIsDeviceActivated();
    }
};

const updateIotDevice = async (assetId, deviceInfo) => {
    const isGamBackend = backendService.isGamBackend();
    const curIotDevice = selectIotDeviceData(getState());
    const {isP4} = curIotDevice?.device || {};
    const deviceData = isGamBackend ? deviceInfo : deviceInfo?.device;
    const {deviceSpecific, firmwareVersion} = deviceData || {};
    const deviceSpecificData = isGamBackend ? deviceSpecific : deviceData;
    const {associatedDevices} = deviceData || {};
    const {
        adaptiveExpManagement,
        pauseHeating,
        engineJoule,
        responsiveDrawProfile,
        vapeCloudSize,
        parentProtection,
        ledIntensity,
        batteryInfo,
        extHapticProfile,
        heatingVibration,
        uiHeatingVibration,
        batteryChargedVibration,
        uiBatteryChargedVibration,
        systemStatus,
        liftUpGesture,
        doubleTapGesture,
        autoStartHeating,
        sessionProfiling,
        deviceSystemError,
        yapActivationStatus,
        configurationCode,
        experienceDataRanges,
        dataIndexesInfo,
        conversionFactor,
        engineKodak,
    } = deviceSpecificData || {};

    if (!yapActivationStatus && isP4) {
        //was in gam. Check if needed
        //appRouterService.forwardToErrorHandlingPage(errorPageTypes.GAM_SYSTEM_ERROR);
        return false;
    }

    await updateActivationStatus(yapActivationStatus);

    await updateDeviceCharacteristics({
        eventType: yapDeviceEventChangeTypes.SYSTEM_STATUS,
        details: systemStatus,
    });

    const state = getState();
    const asset = makeSelectAssetByAssetId(assetId)(state);
    const isHolder = makeSelectIsHolderByAssetId(assetId)(state);
    const {batteryChargeUnified, batteryVoltage} = batteryInfo || {};
    const {batteryVoltageFactor} = conversionFactor || {};
    const material = isGamBackend ? asset.material : deviceInfo?.material;
    const materialId = getMediaId(material);
    const singleMaterialId = getMediaId(material, true);
    const isOriginalDuoDevice = deviceTypesService.isOriginalsDuoDeviceByCodeOrMaterial({configurationCode, isHolder});

    updateExperienceDataRanges(isHolder, experienceDataRanges, dataIndexesInfo?.Usage?.latestIndex);

    connectCareService.updateDeviceErrors(deviceSystemError, isHolder);

    updateMainDeviceData({
        version: firmwareVersion,
        batteryCharge: batteryChargeUnified,
        batteryVoltage,
        batteryVoltageFactor,
        //WebApp gets codentify at start of connection
        //codentify: codentify?.codentify,
        isHolder,
        mediaId: materialId,
        materialColor: material?.materialColor,
        singleMediaId: singleMaterialId,
        engine: associatedDevices?.engine,
        isOriginalDuoDevice,
        engineKodak,
    });

    const isDeviceSupported = marketConfigService.isDeviceSupportedForceCheck(isHolder);

    if (!isDeviceSupported) {
        dispatch(setIsDeviceNotSupported());

        return false;
    }

    const isLeviaEnabledOnMarket = appConfig.getIsLeviaConsumablesEnabled();

    if (isLeviaEnabledOnMarket) {
        log.info(`yapStateService: Status of the engineKodak: ${engineKodak?.featureState}. isHolder: ${isHolder}`);
    }

    const updateSettings = (type, newValue) => {
        updateDeviceSettings({type, newValue, isHolder});
    };

    updateSettings(yapUpdateSettingsTypes.RESPONSIVE_DRAW_PROFILE, responsiveDrawProfile);
    updateSettings(yapUpdateSettingsTypes.VAPE_CLOUD_SIZE, vapeCloudSize);
    updateSettings(yapUpdateSettingsTypes.PARENT_PROTECTION, parentProtection);
    updateSettings(yapUpdateSettingsTypes.LED_INTENSITY, ledIntensity);
    updateSettings(yapUpdateSettingsTypes.HEATING_VIBRATION, heatingVibration);
    updateSettings(yapUpdateSettingsTypes.EXT_HAPTIC_PROFILE, extHapticProfile);
    updateSettings(yapUpdateSettingsTypes.BATTERY_CHARGED_VIBRATION, batteryChargedVibration);
    updateSettings(yapUpdateSettingsTypes.LIFT_UP_GESTURE, liftUpGesture);
    updateSettings(yapUpdateSettingsTypes.DOUBLE_TAP_GESTURE, doubleTapGesture);
    updateSettings(yapUpdateSettingsTypes.AUTO_START_HEATING, autoStartHeating);
    updateSettings(yapUpdateSettingsTypes.UI_BATTERY_CHARGED_VIBRATION, uiBatteryChargedVibration);
    updateSettings(yapUpdateSettingsTypes.UI_HEATING_VIBRATION, uiHeatingVibration);
    updateSettings(yapUpdateSettingsTypes.SESSION_PROFILING, sessionProfiling);
    updateSettings(yapUpdateSettingsTypes.FLEX_PUFF_ENGINE_JOULE, engineJoule);
    updateSettings(yapUpdateSettingsTypes.AEM_FLEX_BATTERY_ADAPTIVE_EXP, adaptiveExpManagement);
    updateSettings(yapUpdateSettingsTypes.AEM_FLEX_BATTERY_PAUSE_HEATING, pauseHeating);

    iotDeviceService.checkIfDeviceIsInSblModeStatic();
    return true;
};

const updateMainDeviceData = ({
    version,
    batteryCharge,
    codentify,
    isHolder,
    mediaId,
    materialColor,
    singleMediaId,
    engine,
    batteryVoltage,
    batteryVoltageFactor,
    isOriginalDuoDevice,
    engineKodak,
}) => {
    const iotDevice = selectIotDeviceData(getState());
    const {device, holder} = iotDevice || {};
    const curDevice = isHolder ? holder : device;
    const type = curDevice?.type;

    if (!device) return;

    const innerData = {
        version: version || curDevice?.version,
        battery_charge: batteryCharge || curDevice?.battery_charge,
        batteryVoltage: batteryVoltage || curDevice?.batteryVoltage,
        batteryVoltageFactor: batteryVoltageFactor || curDevice?.batteryVoltageFactor,
        batteryLastUpdateDate: batteryVoltageFactor ? new Date() : curDevice?.batteryLastUpdateDate,
        engineKodak,
    };

    const isCodentifyValid = productHelpers.isOriginalCodentifyValid(codentify);

    if (
        isOriginalDuoDevice &&
        (type === DEVICE_TYPES.DEV_TYPE_P1V31_CHARGER || type === DEVICE_TYPES.DEV_TYPE_P1V31_HOLDER)
    ) {
        innerData.type = isHolder
            ? DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_HOLDER
            : DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_CHARGER;
        if (!isHolder) {
            dispatch(updateIotDeviceData({holder: {type: DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_HOLDER}}));
        }
    } else if (
        isOriginalDuoDevice === false &&
        (type === DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_CHARGER ||
            type === DEVICE_TYPES.DEV_TYPE_P1V31_ORIGINALS_HOLDER)
    ) {
        innerData.type = isHolder ? DEVICE_TYPES.DEV_TYPE_P1V31_HOLDER : DEVICE_TYPES.DEV_TYPE_P1V31_CHARGER;
        if (!isHolder) {
            dispatch(updateIotDeviceData({holder: {type: DEVICE_TYPES.DEV_TYPE_P1V31_HOLDER}}));
        }
    }

    if (isCodentifyValid) {
        innerData.codentify = codentify;
    }

    //use previous mediaId - icc media priority
    if (!curDevice.mediaId && mediaId) {
        innerData.mediaId = mediaId;

        if (isHolder) {
            innerData.singleHolderMediaId = singleMediaId;
        } else {
            innerData.materialColor = materialColor;
        }
    }

    const data = {
        [isHolder ? 'holder' : 'device']: innerData,
    };

    loadDeviceAssets(data);
    dispatch(updateIotDeviceData(data));

    updateEngineData(isHolder, engine);
};

const updateEngineData = (isHolder, engine) => {
    if (engine) {
        const {isSBLMode} = engine;

        engine.is_device_in_sbl_mode = isSBLMode;
        const engineData = {
            [isHolder ? 'holder_engine' : 'device_engine']: engine,
        };

        dispatch(updateIotDeviceData(engineData));
    }
};

const loadDeviceAssets = (data) => {
    const {device, holder} = data || {};
    const mediaNamesList = deviceAssetService.getDeviceAssetsList([
        device?.mediaId,
        holder?.mediaId,
        holder?.singleHolderMediaId,
    ]);

    if (mediaNamesList.length) {
        iccConsumerProducts.getProductsAssets(mediaNamesList);
    }
};

const updateAssetMedia = (assets) => {
    if (!assets) return;

    const {material, configurationCode, isHolder} = assets || {};
    const isOriginalDuoDevice = deviceTypesService.isOriginalsDuoDeviceByCodeOrMaterial({
        configurationCode,
        material,
        isHolder,
    });

    const materialId = getMediaId(assets.material);
    const singleMaterialId = getMediaId(assets.material, true);

    updateMainDeviceData({
        isHolder,
        mediaId: materialId,
        materialColor: material?.materialColor,
        singleMediaId: singleMaterialId,
        isOriginalDuoDevice,
    });
};

const setProductInfoAssets = (isHolder) => {
    const state = getState();
    const deviceType = isHolder ? selectIotHolderType(state) : selectIotDeviceType(state);
    const productMaterial = makeSelectConsumerProductMaterialByType(isHolder)(state);
    const mediaId = getMediaId(productMaterial, false, deviceType);

    if (!mediaId) return;

    const innerData = {};

    innerData.mediaId = mediaId;

    if (isHolder) {
        innerData.singleHolderMediaId = getMediaId(productMaterial, true, deviceType);
    } else {
        const {materialColor} = productMaterial || {};

        innerData.materialColor = materialColor;
    }

    const data = {
        [isHolder ? 'holder' : 'device']: innerData,
    };

    loadDeviceAssets(data);
    dispatch(updateIotDeviceData(data));
    return true;
};

const getProductInfoAssets = async (isHolderExists, isForceCall) => {
    const isMediaExists = setProductInfoAssets();
    const isHolderMediaExists = !isHolderExists || setProductInfoAssets(true);

    if (isMediaExists && isHolderMediaExists) return;

    const isDCE2 = marketTypeService.isDCE2();
    const isManualRegistrationEnabled = appConfig.getIsManualRegistrationEnabled();
    const isNonMandatoryRegistrationEnabled = appConfig.getIsNonMandatoryRegistrationEnabled();
    const isSkipRequest = isDCE2 && (isManualRegistrationEnabled || !isNonMandatoryRegistrationEnabled);

    if (isSkipRequest && !isForceCall) return;

    const iotDevice = selectIotDeviceData(getState());

    if (!iotDevice) return;

    const {holder, device} = iotDevice;
    const {codentify: holderCodentify, deviceSerialNumber: holderSerialNumber} = holder || {};
    const {codentify: deviceCodentify, deviceSerialNumber} = device || {};
    const isHolderCodentifyValid = productHelpers.isOriginalCodentifyValid(holderCodentify);
    const isDeviceCodentifyValid = productHelpers.isOriginalCodentifyValid(deviceCodentify);
    const deviceSerialNumbers = [];
    const codentifyIds = [];

    if (!isMediaExists) {
        if (isDeviceCodentifyValid) {
            codentifyIds.push(deviceCodentify);
        } else {
            deviceSerialNumbers.push(deviceSerialNumber);
        }
    }

    if (!isHolderMediaExists) {
        if (isHolderCodentifyValid) {
            codentifyIds.push(holderCodentify);
        } else {
            deviceSerialNumbers.push(holderSerialNumber);
        }
    }

    await iccConsumerProducts.getDetailedProductsInfoBySerialNumbers(deviceSerialNumbers);
    await iccConsumerProducts.getDetailedProductsInfoByCodentifyIds(codentifyIds);

    setProductInfoAssets();
    if (holderSerialNumber) {
        setProductInfoAssets(true);
    }
};

export default {
    checkIsDeviceActivated,
    getProductInfoAssets,
    updateActivationStatus,
    updateAssetMedia,
    updateDeviceCharacteristics,
    updateDeviceSettings,
    updateIotDevice,
    updateEngineData,
};
